import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import Reveal from '../components/Reveal';

const ACCENT = '#3b82f6';

const SERVICES = [
  {
    title: 'AI & Automation',
    desc: 'Intelligent systems that automate workflows and surface actionable insights.',
    image: 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=800&h=560&fit=crop',
    slug: 'ai-automation',
  },
  {
    title: 'Custom Software',
    desc: 'Bespoke software built around your exact workflows, internal tools, platforms, and systems engineered from the ground up.',
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&h=560&fit=crop',
    slug: 'custom-software',
  },
  {
    title: 'Web Development',
    desc: 'Scalable, high-performance web applications built with modern frameworks.',
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&h=560&fit=crop',
    slug: 'web-development',
  },
  {
    title: 'Mobile App Development',
    desc: 'Native and cross-platform iOS & Android apps with exceptional UX and performance.',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=560&fit=crop',
    slug: 'mobile-app-development',
  },
  {
    title: 'Cloud & Backend',
    desc: 'Robust cloud infrastructure and backend architectures engineered to scale.',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=560&fit=crop',
    slug: 'cloud-backend',
  },
];

/* ── Service Card ─────────────────────────────────────────────────────── */
function ServiceCard({ service, color, index }) {
  const [hovered, setHovered] = useState(false);
  const link = service.slug ? `/services/${service.slug}` : '/contact';

  return (
    <Reveal direction="up" delay={index * 80}>
      <Link
        to={link}
        style={{ textDecoration: 'none', display: 'block' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div style={{
          position: 'relative',
          borderRadius: '1rem',
          overflow: 'hidden',
          background: '#0f172a',
          border: `1px solid ${hovered ? color + '40' : 'rgba(30,41,59,0.8)'}`,
          transition: 'border-color 0.3s, transform 0.3s, box-shadow 0.3s',
          transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
          boxShadow: hovered ? `0 24px 60px -12px ${color}25, 0 0 0 1px ${color}20` : '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {/* Image */}
          <div style={{ position: 'relative', aspectRatio: '16/10', overflow: 'hidden' }}>
            <img
              src={service.image}
              alt={service.title}
              style={{
                width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                transform: hovered ? 'scale(1.06)' : 'scale(1)',
                transition: 'transform 0.5s ease',
              }}
            />
            {/* Gradient overlay */}
            <div style={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(to bottom, rgba(2,6,23,0) 30%, rgba(2,6,23,0.85) 100%)`,
            }} />
            {/* Color tint on hover */}
            <div style={{
              position: 'absolute', inset: 0,
              background: color + '18',
              opacity: hovered ? 1 : 0,
              transition: 'opacity 0.3s',
            }} />
            {/* Index badge */}
            <div style={{
              position: 'absolute', top: '1rem', left: '1rem',
              fontSize: '0.6rem', fontWeight: 800, color: color,
              letterSpacing: '0.12em', fontFamily: 'monospace',
              background: 'rgba(2,6,23,0.7)', backdropFilter: 'blur(8px)',
              padding: '0.25rem 0.6rem', borderRadius: '9999px',
              border: `1px solid ${color}30`,
            }}>
              {String(index + 1).padStart(2, '0')}
            </div>
            {/* Arrow icon on hover */}
            <div style={{
              position: 'absolute', top: '1rem', right: '1rem',
              width: '2.25rem', height: '2.25rem', borderRadius: '50%',
              background: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: hovered ? 1 : 0, transform: hovered ? 'scale(1)' : 'scale(0.7)',
              transition: 'opacity 0.25s, transform 0.25s',
            }}>
              <ArrowUpRight style={{ width: '1rem', height: '1rem', color: '#fff' }} />
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '1.5rem' }}>
            <h3 style={{
              fontSize: '1rem', fontWeight: 700, color: '#f1f5f9',
              marginBottom: '0.5rem', letterSpacing: '-0.01em',
              transition: 'color 0.2s',
              ...(hovered ? { color: color } : {}),
            }}>
              {service.title}
            </h3>
            <p style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.75, margin: 0 }}>
              {service.desc}
            </p>
            {/* Bottom link */}
            <div style={{
              marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
              fontSize: '0.8rem', fontWeight: 600, color: color,
              opacity: hovered ? 1 : 0.55, transition: 'opacity 0.2s',
            }}>
              {service.slug ? 'Explore service' : 'Start a project'}
              <ArrowRight style={{ width: '0.85rem', height: '0.85rem' }} />
            </div>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
export default function AllServices() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ background: '#020617', color: '#fff', minHeight: '100vh' }}>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section style={{ position: 'relative', minHeight: '62vh', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        {/* Background image grid */}
        <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px', opacity: 0.18 }}>
          {[
            'photo-1677442136019-21780ecad995',
            'photo-1461749280684-dccba630e2f6',
            'photo-1547658719-da2b51169166',
            'photo-1512941937669-90a1b58e7e9c',
            'photo-1451187580459-43490279c0fa',
            'photo-1498050108023-c5249f4df085',
          ].map((id) => (
            <div key={id} style={{ overflow: 'hidden' }}>
              <img src={`https://images.unsplash.com/${id}?w=600&h=400&fit=crop`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
          ))}
        </div>
        {/* Gradient over grid */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #020617 40%, rgba(2,6,23,0.7) 100%)' }} />
        {/* Glow */}
        <div style={{ position: 'absolute', top: '20%', left: '30%', width: '40rem', height: '30rem', background: 'radial-gradient(ellipse, rgba(59,130,246,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', maxWidth: 'min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem)))', margin: '0 auto', padding: '5.5rem clamp(0.75rem, 2vw, 1.5rem) 3rem', width: '100%' }}>
          <Reveal direction="up">
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3b82f6', letterSpacing: '0.18em', textTransform: 'uppercase', display: 'block', marginBottom: '1.25rem' }}>
              What we do
            </span>
          </Reveal>
          <Reveal direction="up" delay={80}>
            <h1 style={{ fontSize: 'clamp(2.75rem, 6vw, 5rem)', fontWeight: 900, lineHeight: 1.0, letterSpacing: '-0.04em', color: '#f8fafc', margin: '0 0 1.5rem' }}>
              Our Services
            </h1>
          </Reveal>
          <Reveal direction="up" delay={160}>
            <p style={{ fontSize: 'clamp(1rem, 1.5vw, 1.2rem)', color: '#64748b', maxWidth: '36rem', lineHeight: 1.75, margin: '0 0 2.5rem' }}>
              Five core services. One team. Whatever you're building, we've shipped it before.
            </p>
          </Reveal>
          <Reveal direction="up" delay={240}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#3b82f6', color: '#fff', padding: '0.9rem 2rem', borderRadius: '0.5rem', fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem', transition: 'background 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#2563eb'}
                onMouseLeave={e => e.currentTarget.style.background = '#3b82f6'}>
                Start a project <ArrowRight style={{ width: '1rem', height: '1rem' }} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Services grid ────────────────────────────────────────────── */}
      <section style={{ padding: '4.5rem 0 5rem', borderTop: '1px solid rgba(30,41,59,0.5)' }}>
        <div style={{ maxWidth: 'min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem)))', margin: '0 auto', padding: '0 clamp(0.75rem, 2vw, 1.5rem)' }}>
          <Reveal direction="up">
            <div style={{ marginBottom: '3rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ width: '2.5rem', height: '2px', background: ACCENT }} />
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: ACCENT, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
                  What we build
                </span>
              </div>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em', margin: 0, lineHeight: 1.1 }}>
                Build what scales
              </h2>
              <p style={{ fontSize: '0.9rem', color: '#475569', marginTop: '0.75rem', maxWidth: '36rem', lineHeight: 1.75 }}>
                End-to-end software engineering, from AI automation and custom platforms to web, mobile, and cloud, backed by one focused team.
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.5rem' }}>
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.title} service={s} color={ACCENT} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ──────────────────────────────────────────────── */}
      <section style={{ padding: '4rem clamp(0.75rem, 2vw, 1.5rem)', borderTop: '1px solid rgba(30,41,59,0.5)', textAlign: 'center' }}>
        <Reveal direction="up">
          <p style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3b82f6', letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: '1rem' }}>Let's get started</p>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em', marginBottom: '1.25rem' }}>
            Not sure where to begin?
          </h2>
          <p style={{ fontSize: '1rem', color: '#475569', maxWidth: '32rem', margin: '0 auto 2.5rem', lineHeight: 1.75 }}>
            Tell us about your project and our team will recommend the right service, no commitment needed.
          </p>
          <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', background: '#3b82f6', color: '#fff', padding: '1rem 2.5rem', borderRadius: '0.5rem', fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem', transition: 'background 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.background = '#2563eb'}
            onMouseLeave={e => e.currentTarget.style.background = '#3b82f6'}>
            Talk to us <ArrowRight style={{ width: '1rem', height: '1rem' }} />
          </Link>
        </Reveal>
      </section>

    </div>
  );
}
